/**
 * PWA Installation Manager
 * Handles app installation prompts, standalone mode optimization, and PWA features
 */

export class PWAInstallManager {
  constructor() {
    this.deferredPrompt = null;
    this.isStandalone = false;
    this.installButton = null;
    this.installBanner = null;
    this.isInstallable = false;
    
    this.init();
  }

  /**
   * Initialize PWA installation features
   */
  init() {
    this.detectStandaloneMode();
    this.setupInstallPromptHandling();
    this.setupInstallUI();
    this.handleShortcutParameters();
    this.optimizeForStandalone();
    
    console.log('PWA Install Manager initialized');
  }

  /**
   * Detect if app is running in standalone mode
   */
  detectStandaloneMode() {
    // Check various ways the app can be in standalone mode
    this.isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    
    if (this.isStandalone) {
      document.body.classList.add('standalone-mode');
      console.log('App is running in standalone mode');
    } else {
      document.body.classList.add('browser-mode');
      console.log('App is running in browser mode');
    }
    
    // Listen for display mode changes
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (e) => {
      this.isStandalone = e.matches;
      if (this.isStandalone) {
        document.body.classList.add('standalone-mode');
        document.body.classList.remove('browser-mode');
        this.hideInstallPrompts();
      } else {
        document.body.classList.add('browser-mode');
        document.body.classList.remove('standalone-mode');
      }
    });
  }

  /**
   * Set up beforeinstallprompt event handling
   */
  setupInstallPromptHandling() {
    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('beforeinstallprompt event fired');
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Store the event so it can be triggered later
      this.deferredPrompt = e;
      this.isInstallable = true;
      
      // Show custom install UI
      this.showInstallPrompt();
    });

    // Listen for the appinstalled event
    window.addEventListener('appinstalled', (e) => {
      console.log('PWA was installed successfully');
      
      // Hide install prompts
      this.hideInstallPrompts();
      
      // Clear the deferredPrompt
      this.deferredPrompt = null;
      this.isInstallable = false;
      
      // Show success message
      this.showInstallSuccessMessage();
      
      // Track installation (if analytics are available)
      this.trackInstallation();
    });

    // Handle iOS Safari install prompt
    this.setupIOSInstallPrompt();
  }

  /**
   * Set up iOS Safari specific install prompt
   */
  setupIOSInstallPrompt() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = window.navigator.standalone;
    
    if (isIOS && !isInStandaloneMode) {
      // Show iOS-specific install instructions
      setTimeout(() => {
        this.showIOSInstallInstructions();
      }, 3000); // Show after 3 seconds
    }
  }

  /**
   * Create and set up install UI elements
   */
  setupInstallUI() {
    // Create install banner
    this.createInstallBanner();
    
    // Create install button (can be placed anywhere in the UI)
    this.createInstallButton();
    
    // Add install option to existing UI if available
    this.addInstallToExistingUI();
  }

  /**
   * Create install banner
   */
  createInstallBanner() {
    this.installBanner = document.createElement('div');
    this.installBanner.id = 'pwa-install-banner';
    this.installBanner.className = 'pwa-install-banner';
    this.installBanner.style.display = 'none';
    
    this.installBanner.innerHTML = `
      <div class="install-banner-content">
        <div class="install-banner-icon">
          <img src="icons/icon-72x72.png" alt="Solar Time App" width="48" height="48">
        </div>
        <div class="install-banner-text">
          <h3>Install Solar Time</h3>
          <p>Get quick access to sunrise and sunset times</p>
        </div>
        <div class="install-banner-actions">
          <button class="install-button primary" id="install-app-button">
            Install
          </button>
          <button class="dismiss-button" id="dismiss-install-button" aria-label="Dismiss install prompt">
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    `;
    
    // Add event listeners
    this.installBanner.querySelector('#install-app-button').addEventListener('click', () => {
      this.triggerInstallPrompt();
    });
    
    this.installBanner.querySelector('#dismiss-install-button').addEventListener('click', () => {
      this.dismissInstallPrompt();
    });
    
    // Add to page
    document.body.appendChild(this.installBanner);
  }

  /**
   * Create install button for integration into existing UI
   */
  createInstallButton() {
    this.installButton = document.createElement('button');
    this.installButton.id = 'pwa-install-button';
    this.installButton.className = 'pwa-install-button';
    this.installButton.style.display = 'none';
    this.installButton.innerHTML = `
      <span class="install-icon" aria-hidden="true">📱</span>
      <span class="install-text">Install App</span>
    `;
    
    this.installButton.addEventListener('click', () => {
      this.triggerInstallPrompt();
    });
    
    // Try to add to header or create a floating button
    const header = document.querySelector('.app-header');
    if (header) {
      header.appendChild(this.installButton);
    } else {
      // Create floating install button
      this.installButton.classList.add('floating-install-button');
      document.body.appendChild(this.installButton);
    }
  }

  /**
   * Add install option to existing UI elements
   */
  addInstallToExistingUI() {
    // Add install option to any existing menu or settings
    // This is a placeholder for future menu integration
    console.log('Install option available for UI integration');
  }

  /**
   * Show install prompt
   */
  showInstallPrompt() {
    if (!this.isStandalone && this.isInstallable) {
      if (this.installBanner) {
        this.installBanner.style.display = 'block';
        
        // Auto-hide after 10 seconds
        setTimeout(() => {
          if (this.installBanner.style.display !== 'none') {
            this.installBanner.style.display = 'none';
          }
        }, 10000);
      }
      
      if (this.installButton) {
        this.installButton.style.display = 'inline-flex';
      }
    }
  }

  /**
   * Hide install prompts
   */
  hideInstallPrompts() {
    if (this.installBanner) {
      this.installBanner.style.display = 'none';
    }
    
    if (this.installButton) {
      this.installButton.style.display = 'none';
    }
  }

  /**
   * Trigger the install prompt
   */
  async triggerInstallPrompt() {
    if (!this.deferredPrompt) {
      console.log('No install prompt available');
      return;
    }

    try {
      // Show the install prompt
      this.deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;
      
      console.log(`User response to install prompt: ${outcome}`);
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
        this.trackInstallDismissal();
      }
      
      // Clear the deferredPrompt
      this.deferredPrompt = null;
      this.isInstallable = false;
      
      // Hide install UI
      this.hideInstallPrompts();
      
    } catch (error) {
      console.error('Error triggering install prompt:', error);
    }
  }

  /**
   * Dismiss install prompt and remember user preference
   */
  dismissInstallPrompt() {
    this.hideInstallPrompts();
    
    // Remember that user dismissed the prompt
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    
    console.log('Install prompt dismissed by user');
  }

  /**
   * Check if user has previously dismissed install prompt
   */
  hasUserDismissedInstall() {
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (!dismissedTime) return false;
    
    // Show prompt again after 7 days
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return parseInt(dismissedTime) > sevenDaysAgo;
  }

  /**
   * Show iOS-specific install instructions
   */
  showIOSInstallInstructions() {
    if (this.hasUserDismissedInstall()) return;
    
    const iosPrompt = document.createElement('div');
    iosPrompt.id = 'ios-install-prompt';
    iosPrompt.className = 'ios-install-prompt';
    
    iosPrompt.innerHTML = `
      <div class="ios-prompt-content">
        <div class="ios-prompt-header">
          <img src="icons/icon-72x72.png" alt="Solar Time App" width="48" height="48">
          <h3>Install Solar Time</h3>
          <button class="close-button" id="close-ios-prompt" aria-label="Close install instructions">
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="ios-prompt-instructions">
          <p>To install this app on your iPhone/iPad:</p>
          <ol>
            <li>Tap the <strong>Share</strong> button <span class="ios-icon">⎋</span></li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong> <span class="ios-icon">⊞</span></li>
            <li>Tap <strong>"Add"</strong> to confirm</li>
          </ol>
        </div>
        <div class="ios-prompt-actions">
          <button class="dismiss-button" id="dismiss-ios-prompt">
            Got it
          </button>
        </div>
      </div>
    `;
    
    // Add event listeners
    iosPrompt.querySelector('#close-ios-prompt').addEventListener('click', () => {
      document.body.removeChild(iosPrompt);
    });
    
    iosPrompt.querySelector('#dismiss-ios-prompt').addEventListener('click', () => {
      this.dismissInstallPrompt();
      document.body.removeChild(iosPrompt);
    });
    
    document.body.appendChild(iosPrompt);
    
    // Auto-hide after 15 seconds
    setTimeout(() => {
      if (document.body.contains(iosPrompt)) {
        document.body.removeChild(iosPrompt);
      }
    }, 15000);
  }

  /**
   * Handle URL parameters for app shortcuts
   */
  handleShortcutParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Handle focus parameter for shortcuts
    const focus = urlParams.get('focus');
    if (focus) {
      this.focusOnElement(focus);
    }
    
    // Handle action parameter for shortcuts
    const action = urlParams.get('action');
    if (action) {
      this.handleShortcutAction(action);
    }
  }

  /**
   * Focus on specific element based on shortcut
   * @param {string} element - Element to focus on
   */
  focusOnElement(element) {
    setTimeout(() => {
      let targetElement;
      
      switch (element) {
        case 'sunrise':
          targetElement = document.getElementById('sunrise-delta');
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            targetElement.classList.add('highlighted');
            setTimeout(() => targetElement.classList.remove('highlighted'), 3000);
          }
          break;
          
        case 'sunset':
          targetElement = document.getElementById('sunset-delta');
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            targetElement.classList.add('highlighted');
            setTimeout(() => targetElement.classList.remove('highlighted'), 3000);
          }
          break;
          
        default:
          console.log(`Unknown focus element: ${element}`);
      }
    }, 1000); // Wait for app to load
  }

  /**
   * Handle shortcut actions
   * @param {string} action - Action to perform
   */
  handleShortcutAction(action) {
    setTimeout(() => {
      switch (action) {
        case 'location':
          // Open location settings/manual entry
          const locationButton = document.getElementById('manual-location-button');
          if (locationButton) {
            locationButton.click();
          }
          break;
          
        default:
          console.log(`Unknown shortcut action: ${action}`);
      }
    }, 1000); // Wait for app to load
  }

  /**
   * Optimize app for standalone mode
   */
  optimizeForStandalone() {
    if (this.isStandalone) {
      // Add standalone-specific optimizations
      this.addStandaloneStyles();
      this.optimizeStandaloneNavigation();
      this.handleStandaloneStatusBar();
    }
  }

  /**
   * Add standalone-specific styles
   */
  addStandaloneStyles() {
    // Add CSS for standalone mode if not already present
    if (!document.getElementById('standalone-styles')) {
      const style = document.createElement('style');
      style.id = 'standalone-styles';
      style.textContent = `
        .standalone-mode {
          /* Add padding for status bar on iOS */
          padding-top: env(safe-area-inset-top);
          padding-bottom: env(safe-area-inset-bottom);
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }
        
        .standalone-mode .app-header {
          /* Adjust header for standalone mode */
          padding-top: calc(env(safe-area-inset-top) + 10px);
        }
        
        .pwa-install-banner,
        .pwa-install-button,
        .ios-install-prompt {
          /* Hide install prompts in standalone mode */
          display: none !important;
        }
        
        .highlighted {
          animation: highlight-pulse 3s ease-in-out;
          background-color: rgba(255, 215, 0, 0.2);
          border-radius: 8px;
          padding: 8px;
          margin: -8px;
        }
        
        @keyframes highlight-pulse {
          0%, 100% { background-color: transparent; }
          50% { background-color: rgba(255, 215, 0, 0.3); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Optimize navigation for standalone mode
   */
  optimizeStandaloneNavigation() {
    // Prevent external links from opening in the PWA
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.hostname !== window.location.hostname) {
        e.preventDefault();
        window.open(link.href, '_blank');
      }
    });
  }

  /**
   * Handle status bar styling in standalone mode
   */
  handleStandaloneStatusBar() {
    // Update theme-color meta tag based on current theme
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      // Could be dynamic based on time of day or user preference
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      themeColorMeta.content = isDarkMode ? '#1a1a1a' : '#ffffff';
    }
  }

  /**
   * Show install success message
   */
  showInstallSuccessMessage() {
    const successMessage = document.createElement('div');
    successMessage.className = 'install-success-message';
    successMessage.innerHTML = `
      <div class="success-content">
        <span class="success-icon" aria-hidden="true">✅</span>
        <span class="success-text">Solar Time installed successfully!</span>
      </div>
    `;
    
    document.body.appendChild(successMessage);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (document.body.contains(successMessage)) {
        document.body.removeChild(successMessage);
      }
    }, 5000);
  }

  /**
   * Track installation for analytics (placeholder)
   */
  trackInstallation() {
    console.log('PWA installation tracked');
    // Add analytics tracking here if needed
  }

  /**
   * Track install dismissal for analytics (placeholder)
   */
  trackInstallDismissal() {
    console.log('PWA install dismissal tracked');
    // Add analytics tracking here if needed
  }

  /**
   * Check if PWA features are supported
   */
  static isSupported() {
    return 'serviceWorker' in navigator && 'BeforeInstallPromptEvent' in window;
  }

  /**
   * Get installation status
   */
  getInstallationStatus() {
    return {
      isStandalone: this.isStandalone,
      isInstallable: this.isInstallable,
      hasPrompt: !!this.deferredPrompt,
      isSupported: PWAInstallManager.isSupported()
    };
  }
}

export default PWAInstallManager;